import React, { useEffect, useMemo, useState } from "react"

import { useNavigate } from "react-router-dom"

import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import {
    COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG,
    OneVerifyEraUploadFileFormat,
} from "@common-shared/config/oneVerify/oneVerifyIntegrations.config"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"
import { OneVerifyIntegrationCategory } from "@common-shared/types/integrations.types"

import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { SpinnerElement } from "@front-shared/elements/spinner.element"
import { useIsMobile } from "@front-shared/hooks/isMobile.hook"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook"

import { GENERAL_CONFIG } from "../../config/general.config"
import {
    EraUploadIntegrationTab,
    INTEGRATIONS_CONFIG,
} from "../../config/integrations.config"
import { ROUTES_CONFIG } from "../../config/routes.config"
import { SelectTabsComponent } from "../../elements/selectTabs.component"
import { InsightsHelper } from "../../helpers/insights.helper"
import { useHasPermissions } from "../../hooks/hasPermissions.hook"
import { useIsFeatureFlagEnabled } from "../../hooks/isFeatureFlagEnabled.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import type { IOneVerifyIntegration } from "../../types/integrations.types"
import type {
    IEraUploadBatchReviewState,
    IEraUploadCompletionState,
} from "../../types/integrations.types"
import { RequestIntegrationPopupComponent } from "../popups/requestIntegrationPopup.component"
import { EraUploadCompleteComponent } from "./eraUpload/eraUploadComplete.component"
import { EraUploadReviewComponent } from "./eraUpload/eraUploadReview.component"
import { IntegrationDetailComponent } from "./integrationDetail.component"
import { IntegrationsListComponent } from "./integrationsList.component"
import { UploadEraIntegrationDetailComponent } from "./uploadEraIntegrationDetail.component"

export const IntegrationsComponent: React.FunctionComponent = () => {
    useScrollFix()

    const navigate = useNavigate()

    const isMobile = useIsMobile()

    useHasPermissions(OneVerifyPermission.managePracticeIntegrations)

    useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_INTEGRATIONS,
        undefined,
        ROUTES_CONFIG.profileUrl,
    )

    const isCsvUploadEnabled =
        useIsFeatureFlagEnabled(FeatureFlag.ONE_VERIFY_ERA_UPLOAD_CSV) ?? false

    const runtimeState = useAppSelector(runtimeSelector)

    const [activeCategory, setActiveCategory] =
        useState<OneVerifyIntegrationCategory>(
            OneVerifyIntegrationCategory.eraData,
        )

    const [selectedIntegration, setSelectedIntegration] =
        useState<IOneVerifyIntegration | null>(null)

    const [
        isRequestIntegrationPopupActive,
        setIsRequestIntegrationPopupActive,
    ] = useState<boolean>(false)

    const [eraUploadReviewState, setEraUploadReviewState] =
        useState<IEraUploadBatchReviewState | null>(null)

    const [eraUploadCompletionState, setEraUploadCompletionState] =
        useState<IEraUploadCompletionState | null>(null)

    const [uploadEraInitialTab, setUploadEraInitialTab] = useState<
        EraUploadIntegrationTab | undefined
    >(undefined)

    const integrationsForCategory = useMemo(() => {
        switch (activeCategory) {
            case OneVerifyIntegrationCategory.eraData:
                return INTEGRATIONS_CONFIG.eraIntegrations
            case OneVerifyIntegrationCategory.ehr:
                return INTEGRATIONS_CONFIG.ehrIntegrations
        }
    }, [activeCategory])

    const isUploadEraSelected =
        selectedIntegration?.id ===
        COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload.integrationId

    const isReviewActive =
        !!eraUploadReviewState &&
        isUploadEraSelected &&
        !eraUploadCompletionState

    const isUploadCompleteActive =
        !!eraUploadCompletionState && isUploadEraSelected

    useEffect(() => {
        setSelectedIntegration(integrationsForCategory[0] ?? null)
        setEraUploadReviewState(null)
        setEraUploadCompletionState(null)
        setUploadEraInitialTab(undefined)
    }, [integrationsForCategory])

    useEffect(() => {
        if (!isUploadEraSelected) {
            setEraUploadReviewState(null)
            setEraUploadCompletionState(null)
            setUploadEraInitialTab(undefined)
        }
    }, [isUploadEraSelected])

    const pageTitle = isReviewActive
        ? "Review ERA Upload"
        : isUploadCompleteActive
          ? "Upload Complete"
          : "Integrations"

    return runtimeState.enabledFeatureFlags ? (
        <div
            data-produktly="integrations-page"
            className="pb-32 relative max-w-full"
        >
            <PageHelmetElement
                title={pageTitle}
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            <div
                data-produktly="integrations-header"
                className="max-w-[960px] w-full overflow-x-hidden"
            >
                <PrimaryText
                    className="sm:mt-[64px]"
                    typography={isMobile ? "h2" : "h1"}
                >
                    Integrations
                </PrimaryText>

                <PrimaryText className="mt-[8px] max-w-[640px]" weight="medium">
                    Integrate your data sources to power ERA ingestion and
                    improve coverage accuracy.
                </PrimaryText>
            </div>

            {isUploadCompleteActive && eraUploadCompletionState ? (
                <EraUploadCompleteComponent
                    className="mt-[24px]"
                    state={eraUploadCompletionState}
                    onUploadMore={() => {
                        setEraUploadCompletionState(null)

                        setUploadEraInitialTab(
                            eraUploadCompletionState.fileFormat ===
                                OneVerifyEraUploadFileFormat.csv
                                ? EraUploadIntegrationTab.csv
                                : EraUploadIntegrationTab.x12,
                        )
                    }}
                    onViewUploadedFiles={() => {
                        setEraUploadCompletionState(null)

                        navigate(
                            InsightsHelper.buildEraUploadStatusInsightsUrl(),
                        )
                    }}
                />
            ) : isReviewActive && eraUploadReviewState ? (
                <EraUploadReviewComponent
                    className="mt-[24px]"
                    state={eraUploadReviewState}
                    onBatchChange={setEraUploadReviewState}
                    onClose={() => {
                        setEraUploadReviewState(null)
                    }}
                    onUploadComplete={completionState => {
                        setEraUploadReviewState(null)

                        setEraUploadCompletionState(completionState)
                    }}
                />
            ) : (
                <div className="max-w-[960px] w-full overflow-x-hidden">
                    <div
                        data-produktly="integrations-categories"
                        className="mt-[24px] w-fit"
                    >
                        <SelectTabsComponent
                            ariaLabel="Integration category"
                            items={INTEGRATIONS_CONFIG.displayCategories}
                            value={activeCategory}
                            onChange={setActiveCategory}
                        />
                    </div>

                    <IntegrationsListComponent
                        className="mt-[16px]"
                        integrations={integrationsForCategory}
                        selectedIntegration={selectedIntegration}
                        onSelectIntegration={setSelectedIntegration}
                        onRequestIntegration={() => {
                            setIsRequestIntegrationPopupActive(true)
                        }}
                    />

                    <RequestIntegrationPopupComponent
                        isActive={isRequestIntegrationPopupActive}
                        integrationCategory={activeCategory}
                        onDone={() => {
                            setIsRequestIntegrationPopupActive(false)
                        }}
                    />

                    {isUploadEraSelected && selectedIntegration ? (
                        <UploadEraIntegrationDetailComponent
                            key={`${selectedIntegration.id}-${uploadEraInitialTab ?? "default"}`}
                            className="mt-[24px]"
                            integration={selectedIntegration}
                            isCsvUploadEnabled={isCsvUploadEnabled}
                            initialTab={uploadEraInitialTab}
                            onValidationDone={setEraUploadReviewState}
                        />
                    ) : (
                        <IntegrationDetailComponent
                            key={selectedIntegration?.id.toString()}
                            className="mt-[24px]"
                            integration={selectedIntegration}
                        />
                    )}
                </div>
            )}
        </div>
    ) : (
        <SpinnerElement containerClassName="text-center mt-[48px]" />
    )
}
